import { Code, Text } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import GraphqlError from '@components/graphql-error';
import LoadingData from '@components/graphql-loading-data';

import { GET_ITINERARY_VERSIONS } from '@queries/itinerary-versions';

// Safely parse a string if it looks like JSON
function parseIfJsonString<T = unknown>(val: unknown): T | unknown {
  if (typeof val !== 'string') return val;
  const s = val.trim();
  if (!((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']')))) return val;
  try {
    return JSON.parse(s) as T;
  } catch {
    return val;
  }
}

// Recursively parse any JSON-looking strings within objects/arrays (limited depth)
function deepParseJsonStrings(val: unknown, depth = 2): unknown {
  if (depth < 0) return val;
  const first = parseIfJsonString(val);
  if (first !== val) {
    return deepParseJsonStrings(first, depth - 1);
  }
  if (Array.isArray(first)) {
    return first.map((v) => deepParseJsonStrings(v, depth));
  }
  if (first && typeof first === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(first as Record<string, unknown>)) {
      out[k] = deepParseJsonStrings(v, depth);
    }
    return out;
  }
  return first;
}

function ItineraryVersions() {
  const { tripProfileId } = useParams();
  // const tripProfileId = '2803d03e-0868-46d9-b37b-b06eddbb10d7';

  const { loading, error, data } = useQuery(GET_ITINERARY_VERSIONS, {
    variables: {
      tripProfileId: tripProfileId || '',
    },
  });
  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;
  if (!tripProfileId) return <Text>Use /admin/itinerary-versions/tripProfileId </Text>;

  // Pick the item with the highest "version"
  type ItineraryVersion = {
    version: number | string;
    messages?: unknown;
    // Add other known fields here if needed, e.g. id?: string;
    [key: string]: unknown;
  };

  const versions = (data?.getItineraryVersions ?? []) as ItineraryVersion[];

  const latestVersion = versions.reduce<(typeof versions)[number] | null>((max, item) => {
    const v = typeof item.version === 'string' ? parseFloat(item.version) : (item.version ?? -Infinity);
    const maxV = max
      ? typeof max.version === 'string'
        ? parseFloat(max.version)
        : (max.version ?? -Infinity)
      : -Infinity;
    return v > maxV ? item : max;
  }, null);

  if (!latestVersion) return <Text>No itinerary versions found.</Text>;

  // Normalize messages: handle stringified JSON (even double-stringified), and parse nested JSON-looking strings like "content"
  const { messages } = latestVersion;
  const normalizedMessages = deepParseJsonStrings(messages);

  return (
    <div>
      <Code block>{JSON.stringify(normalizedMessages, null, 2)}</Code>
    </div>
  );
}

export default ItineraryVersions;
